import React, { Component, PropsWithChildren } from 'react';
import Button from '../Button/Button';
import DatePicker from '../DateTimePicker/DateTimePicker';
import RangeDefinitionButton from './RangeDefinitionButton';
import { IDateRangeEditorProps, IDateType, IRangeDefinition } from './types';

// context aware Localized formatting strings
const raw = {
  edit: {
    date: 'MMM d, yyyy', // Sep 4, 1986
    dateTime: 'E, MMM d, yyyy h:mm a', // Thu, Sep 4, 1986 8:30 PM
    time: 'h:mm a',
  },
  format: {
    date: 'MMM d, yyyy', // Sep 4, 1986
    dateTime: 'E, MMM d, yyyy h:mm a', // Thu, Sep 4, 1986 8:30 PM
    time: 'h:mm a', // 8:30 PM
  },
};

export interface IDateRangeEditorState {
  start: IDateType;
  end: IDateType;
  isValid: boolean;
}

/**
 * todo: from/to/apply/cancel need to be localized
 * todo: the named ranges need to be localized
 */
export class DateRangeEditor extends Component<PropsWithChildren<IDateRangeEditorProps>, IDateRangeEditorState> {
  public static defaultProps = {
    displaySuccess: false,
    name: { start: '', end: '' },
    onCancel: () => '',
  };

  public state = {
    end: this.props.value ? this.props.value.end || null : null,
    isValid: this.props.value
      ? typeof this.props.value.start === 'object' && typeof this.props.value.end === 'object'
      : false,
    start: this.props.value ? this.props.value.start || null : null,
  };

  // create static formatter functions one time
  static get raw() {
    return raw;
  }

  public handleChange = ({ startDate, endDate }: { startDate?: IDateType; endDate?: IDateType }) => {
    const start = startDate === undefined ? this.state.start : startDate;
    let end = endDate === undefined ? this.state.end : endDate;

    // adjust if start is after end
    if (start && end && start.getTime() > end.getTime()) {
      end = new Date(start.getTime());
    }

    let isValid = false;
    if (start === null && end === null) {
      isValid = true;
    } else if (start instanceof Date && end instanceof Date) {
      isValid = true;
    }
    this.setState({ isValid, start, end });
  };

  public handleChangeStart = (name: string, startDate: IDateType) => this.handleChange({ startDate });
  public handleChangeEnd = (name: string, endDate: IDateType) => this.handleChange({ endDate });
  public handleNamedRange = (range: IRangeDefinition) => {
    this.handleChange({ startDate: range.start, endDate: range.end });
  };

  private handleClear = () => {
    this.handleChange({ startDate: null, endDate: null });
  };

  public handleApply = () => {
    const { start, end } = this.state;
    this.props.onChange &&
      this.props.onChange(this.props.name, {
        end,
        start,
      });
  };

  // returns a <DatePicker> for the start or the end
  public datePicker(type: 'start' | 'end') {
    const { name, value, namedRanges, onCancel, onChange, ...props } = this.props;
    const isStart = type === 'start';
    const handleChange = isStart ? this.handleChangeStart : this.handleChangeEnd;

    return (
      <div className="hlc-picker-wrapper">
        <DatePicker
          {...props}
          valid={true}
          label={false}
          name={isStart ? name.start : name.end}
          value={this.state[type]}
          onChange={handleChange}
          editDateFormat={raw.edit}
          viewDateFormat={raw.format}
        />
        <span className={'hlc-drp-input-prefix'}>{isStart ? 'From' : 'To'}</span>
      </div>
    );
  }

  public maybeNamedRanges() {
    const { namedRanges } = this.props;
    if (namedRanges) {
      const ranges = namedRanges.map((range) => {
        return (
          <RangeDefinitionButton
            range={range}
            onClick={this.handleNamedRange}
            key={typeof range === 'object' ? range.label : range.toString()}
          />
        );
      });
      return <div className="hlc-drp-namedranges">{ranges}</div>;
    } else {
      return null;
    }
  }

  public render() {
    return (
      <div className={'hlc-drp-editor clearfix'}>
        {this.datePicker('start')}
        {this.datePicker('end')}
        {this.maybeNamedRanges()}
        <div className={'pull-right'}>
          <Button label="Cancel" color="cancel" onClick={this.props.onCancel} />{' '}
          <Button label="Clear" color="cancel" type="button" onClick={this.handleClear} />{' '}
          <Button label="Apply" disabled={!this.state.isValid} color="primary" onClick={this.handleApply} />{' '}
        </div>
      </div>
    );
  }
}

export default DateRangeEditor;
